<script setup lang="ts">
/**
 * One modal for all in-app feedback: bug report, idea, app rating.
 *
 * `prompted` = opened by the one-week rating prompt (SidebarLayout): only the
 * rating tab shows and closing it without sending SNOOZES the prompt, so it
 * doesn't reappear on the next page load. Rating consent to publish is never
 * pre-ticked — the row feeds landing-page testimonials only with a yes.
 */
import client from '~/api/client'
import { useWebStore } from '~/store/web'

type Kind = 'bug' | 'idea' | 'rating'

const props = withDefaults(defineProps<{ initialKind?: Kind; prompted?: boolean }>(), {
  initialKind: 'bug',
  prompted: false,
})
const emit = defineEmits<{ (e: 'close', result: 'sent' | 'snoozed' | 'dismissed'): void }>()

const route = useRoute()
const webStore = useWebStore()

const kind = ref<Kind>(props.prompted ? 'rating' : props.initialKind)
const message = ref('')
const rating = ref(0)
const hoverRating = ref(0)
const allowPublic = ref(false)
const displayName = ref('')
const sending = ref(false)
const error = ref('')
const sent = ref(false)

const tabs: { id: Kind; label: string }[] = [
  { id: 'bug', label: 'Nahlásit chybu' },
  { id: 'idea', label: 'Navrhnout nápad' },
  { id: 'rating', label: 'Ohodnotit' },
]

const placeholder = computed(
  () =>
    ({
      bug: 'Co se stalo? Co jste dělali těsně předtím a co jste čekali, že se stane?',
      idea: 'Co vám v aplikaci chybí nebo co by šlo udělat lépe?',
      rating: 'Co se vám líbí, co byste zlepšili? (nepovinné)',
    })[kind.value],
)

const canSend = computed(() => (kind.value === 'rating' ? rating.value >= 1 : message.value.trim().length > 0))

const starLabels = ['', 'Špatné', 'Nic moc', 'Ujde to', 'Dobré', 'Skvělé']

async function send() {
  if (!canSend.value || sending.value) return
  sending.value = true
  error.value = ''
  const { error: err } = await client.POST('/api/feedback/', {
    body: {
      kind: kind.value,
      message: message.value,
      rating: kind.value === 'rating' ? rating.value : null,
      page_url: route.fullPath,
      web_id: webStore.selectedWebsite?.id ?? null,
      allow_public: kind.value === 'rating' && allowPublic.value,
      display_name: kind.value === 'rating' ? displayName.value : '',
    },
  })
  sending.value = false
  if (err) {
    error.value = String((err as { detail?: string })?.detail ?? 'Odeslání se nepovedlo, zkuste to prosím znovu.')
    return
  }
  sent.value = true
}

async function snooze() {
  await client.POST('/api/feedback/prompt/snooze/', { body: { days: 7 } })
  emit('close', 'snoozed')
}

function close() {
  if (sent.value) return emit('close', 'sent')
  if (props.prompted) return snooze()
  emit('close', 'dismissed')
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <!-- Teleport: the toolbar has backdrop-filter, which makes it the containing
       block for position:fixed — the modal would be clipped to the header. -->
  <Teleport to="body">
    <div class="fb-backdrop" @click.self="close">
      <div class="fb-card" role="dialog" aria-modal="true">
        <button type="button" class="fb-x" aria-label="Zavřít" @click="close">×</button>

        <template v-if="sent">
          <div class="fb-done">
            <div class="fb-done__icon">✓</div>
            <h3 class="fb-title">Děkujeme!</h3>
            <p class="fb-sub">
              <template v-if="kind === 'bug'">Chybu jsme dostali a podíváme se na ni.</template>
              <template v-else-if="kind === 'idea'">Nápad jsme si zapsali. Každý čteme.</template>
              <template v-else>Vaše hodnocení nám moc pomáhá.</template>
            </p>
            <button type="button" class="wb-btn" @click="emit('close', 'sent')">Zavřít</button>
          </div>
        </template>

        <template v-else>
          <h3 class="fb-title">
            {{ prompted ? 'Jak se vám s qCMS pracuje?' : 'Zpětná vazba' }}
          </h3>
          <p class="fb-sub">
            <template v-if="prompted">Používáte aplikaci už týden. Dáte nám pár hvězdiček?</template>
            <template v-else>Narazili jste na chybu, nebo vám něco chybí? Napište nám.</template>
          </p>

          <div v-if="!prompted" class="fb-tabs">
            <button
              v-for="t in tabs"
              :key="t.id"
              type="button"
              class="fb-tab"
              :class="{ active: kind === t.id }"
              @click="kind = t.id"
            >
              {{ t.label }}
            </button>
          </div>

          <div v-if="kind === 'rating'" class="fb-stars" @mouseleave="hoverRating = 0">
            <button
              v-for="n in 5"
              :key="n"
              type="button"
              class="fb-star"
              :class="{ on: n <= (hoverRating || rating) }"
              :aria-label="`${n} z 5`"
              @mouseenter="hoverRating = n"
              @click="rating = n"
            >
              <svg width="30" height="30" viewBox="0 0 24 24">
                <path
                  d="M12 2.8l2.8 5.7 6.3.9-4.55 4.43 1.07 6.27L12 17.14l-5.62 2.96 1.07-6.27L2.9 9.4l6.3-.9z"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <span class="fb-stars__label">{{ starLabels[hoverRating || rating] }}</span>
          </div>

          <textarea
            v-model="message"
            class="fb-input fb-textarea"
            :placeholder="placeholder"
            rows="5"
            maxlength="5000"
          />

          <template v-if="kind === 'rating'">
            <label class="fb-check">
              <input v-model="allowPublic" type="checkbox" />
              <span>Souhlasím se zveřejněním hodnocení (se jménem níže a názvem mého webu) na webu qCMS</span>
            </label>
            <input
              v-if="allowPublic"
              v-model="displayName"
              class="fb-input"
              placeholder="Jméno pod hodnocením (např. Jana z Kavárny U Mlýna)"
              maxlength="120"
            />
          </template>
          <p v-else class="fb-hint">Automaticky přiložíme stránku, na které jste, a vybraný web.</p>

          <p v-if="error" class="fb-error">{{ error }}</p>

          <div class="fb-footer">
            <button v-if="prompted" type="button" class="wb-btn text" @click="snooze">Připomenout později</button>
            <button v-else type="button" class="wb-btn ghost" @click="close">Zrušit</button>
            <button type="button" class="wb-btn" :disabled="!canSend || sending" @click="send">
              {{ sending ? 'Odesílám…' : 'Odeslat' }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.fb-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(31, 27, 22, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
}
.fb-card {
  position: relative;
  background: var(--paper);
  border: 1px solid var(--hair);
  border-radius: var(--r-xl);
  padding: 28px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 20px 60px rgba(31, 27, 22, 0.25);
  color: var(--ink);
}
.fb-x {
  position: absolute;
  top: 12px;
  right: 14px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: var(--ink-3);
  cursor: pointer;
  padding: 4px 8px;
}
.fb-x:hover {
  color: var(--ink);
}
.fb-title {
  margin: 0 0 4px;
  font-family: var(--serif);
  font-size: 22px;
}
.fb-sub {
  margin: 0 0 16px;
  font-size: 13.5px;
  color: var(--ink-3);
}
.fb-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--hair);
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.fb-tab {
  padding: 8px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-3);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  white-space: nowrap;
  font-family: var(--sans);
}
.fb-tab:hover {
  color: var(--ink-2);
}
.fb-tab.active {
  color: var(--ink);
  border-bottom-color: var(--accent);
}
.fb-stars {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 14px;
}
.fb-star {
  border: none;
  background: transparent;
  padding: 2px;
  cursor: pointer;
  line-height: 0;
}
.fb-star path {
  fill: transparent;
  stroke: var(--hair-2, #c8ccd8);
  stroke-width: 1.5;
  transition: fill 100ms ease;
}
.fb-star.on path {
  fill: #f2b01e;
  stroke: #e0a012;
}
.fb-stars__label {
  margin-left: 10px;
  font-size: 13px;
  color: var(--ink-2);
  font-weight: 600;
}
.fb-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--hair);
  border-radius: var(--r-md);
  background: var(--paper);
  color: var(--ink);
  padding: 10px 12px;
  font-size: 14px;
  font-family: var(--sans);
  margin-bottom: 10px;
}
.fb-input:focus {
  outline: none;
  border-color: var(--accent);
}
.fb-textarea {
  resize: vertical;
  min-height: 110px;
}
.fb-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink-2);
  margin-bottom: 10px;
  cursor: pointer;
}
.fb-hint {
  font-size: 12px;
  color: var(--ink-3);
  margin: 0;
}
.fb-error {
  font-size: 13px;
  color: #b3261e;
  margin: 8px 0 0;
}
.fb-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}
.fb-done {
  text-align: center;
  padding: 12px 0 4px;
}
.fb-done__icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6f2e9;
  color: #3c7a4b;
  font-size: 24px;
  font-weight: 700;
}
</style>
